import type { ComponentType } from 'react';
import type { CUSTOM_GROUP_NAME, FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import type { StarfishIconRef } from '@/app/common/icons/starfish';

export enum FILTER_TYPE {
  SWITCH = 'switch',
  MULTI_SELECT = 'multiSelect',
  RANGE = 'range',
  RADIO = 'radio',
  SEARCH = 'search',
  SEARCH_DYNAMIC = 'searchDynamic',
}

export type FilterDataSwitch = {
  label: string;
  // Same copy with the markers intact; only the switch renders them, pills and chips use label.
  labelEmphasis?: string;
  selected: boolean;
  counter?: number;
  /** A named icon, or the basename of a multi-colour asset under `img/icons/` that no single-colour glyph can reproduce. */
  icon?: StarfishIconRef | string;
  tooltip?: string;
};
// -----------------------------------------------------
export type LabelComponent = {
  componentName: string;
  props: { [key: string]: string | number };
};
export type FilterDataOption = {
  label: string;
  labelComponent?: LabelComponent;
  selected: boolean;
  disabled?: boolean;
  value: string;
  counter?: number;
  isDefault?: boolean;
};
export type FilterDataMultiSelect = {
  label?: string;
  options: FilterDataOption[];
  defaultValue?: string; // When the option with a value is equal to the default value is selected, the option is shown as selected but the url parameter is not created (no need)
};
export type FilterDataRadio = {
  label?: string;
  options: FilterDataOption[];
  defaultValue?: string; // When the option with a value is equal to the default value is selected, the option is shown as selected but the url parameter is not created (no need)
};

// -----------------------------------------------------
export type FilterDataSearch = {
  placeholder: string;
  noResultsLabel: string;
  options: FilterDataOption[];
};
export type FilterDataSearchDynamic = {
  label: string;
  placeholder: string;
  noResultsLabel?: string;
  value: string;
};
// -----------------------------------------------------
export type FilterDataRangeDynamicValues = {
  from: number;
  to: number;
  interval: number;
  diagram: number[];
};
export type FilterDataRange = {
  label: string;
  default: {
    min: number;
    max: number;
  };
  allowed: {
    min: number;
    max: number;
  };
  value: {
    min: number;
    max: number;
  };
  maxValue: number;
  dynamicValues?: FilterDataRangeDynamicValues;
};
// -----------------------------------------------------

export type FilterSelection = { groupId: string; value: string; selected: boolean };

export type Filter = {
  name: FILTER_NAME;
  type: FILTER_TYPE;
  disabled?: boolean;
  data:
    | FilterDataSwitch
    | FilterDataMultiSelect
    | FilterDataRadio
    | FilterDataSearch
    | FilterDataSearchDynamic
    | FilterDataRange;
};

export type FiltersGroup = {
  id: FiltersGroupId;
  label: string;
  filters: Filter[];
  showMore?: boolean;
};

export type CustomGroup = {
  id: FiltersGroupId;
};

export type FiltersGroupId = FILTER_NAME | CUSTOM_GROUP_NAME;

export type FiltersGroups = (FiltersGroup | CustomGroup)[];

export type FilterSectionItem = {
  id: number;
  name: string;
  translationKey: string;
  analyticsValue: string;
};
export type FacilitiesSection = {
  labelKey: string;
  items: FilterSectionItem[];
};

export type SimpleFilterValueList = { [key: string]: number | undefined };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type CustomLabelComponents = Record<string, ComponentType<any>>;
